<template>
  <article class="cr-row txn-row" role="listitem">
    <div class="txn-row__date">
      <span class="cr-row__field-label">Date</span>
      <div class="txn-row__date-text">
        <span class="cr-row__date">{{ dateLabel }}</span>
        <span class="cr-row__clock">{{ timeLabel }}</span>
      </div>
    </div>

    <div class="txn-row__type">
      <span class="cr-row__field-label">Type</span>
      <span
        v-if="isInfoTransactionType(transaction.transactionType)"
        class="txn-row__empty"
      >—</span>
      <span
        v-else-if="transaction.transactionType"
        class="txn-pill"
        :class="transactionTypePillClass(transaction.transactionType)"
      >
        {{ formatLabel(transaction.transactionType) }}
      </span>
      <span v-else class="txn-row__empty">—</span>
    </div>

    <div class="txn-row__category">
      <span class="cr-row__field-label">Category</span>
      <span
        v-if="transaction.deductionType"
        class="txn-pill"
        :class="deductionTypePillClass(transaction.deductionType)"
      >
        <v-icon
          :icon="deductionTypeIcon(transaction.deductionType)"
          size="12"
          class="txn-pill__icon"
          color="currentColor"
        />
        {{ formatLabel(transaction.deductionType) }}
      </span>
      <span v-else class="txn-row__empty">—</span>
    </div>

    <div class="txn-row__amount">
      <span class="cr-row__field-label">Credits</span>
      <span class="txn-amount" :class="amountClass(transaction)">
        {{ formatTransactionAmount(transaction) }}
      </span>
    </div>

    <div class="txn-row__duration">
      <span class="cr-row__field-label">Call duration</span>
      <span class="txn-duration">{{ durationLabel }}</span>
    </div>

    <div class="txn-row__balance">
      <span class="cr-row__field-label">Balance</span>
      <span
        v-if="hasCreditBalance(transaction.balanceAfter)"
        class="txn-balance"
        :class="creditBalanceClass(transaction.balanceAfter)"
        :title="creditBalanceTitle(transaction.balanceAfter)"
      >
        {{ formatBalanceDisplay(transaction.balanceAfter) }}
      </span>
      <span v-else class="txn-row__empty">—</span>
    </div>
  </article>
</template>

<script setup>
import { computed } from 'vue'
import {
  formatCallDuration,
  formatIsoDate,
  formatLabel,
  formatTimeAmPm,
} from '@/utils/format'

defineOptions({ name: 'TransactionsRow' })

const props = defineProps({
  transaction: { type: Object, required: true },
  organization: { type: Object, default: () => ({}) },
})

const dateLabel = computed(() => formatIsoDate(props.transaction.createdOn))
const timeLabel = computed(() => formatTimeAmPm(props.transaction.createdOn))
const durationLabel = computed(() =>
  formatCallDuration(props.transaction.duration_seconds),
)

function isInfoTransactionType(type) {
  return String(type ?? '').toUpperCase() === 'INFO'
}

function transactionTypePillClass(type) {
  const key = String(type ?? '').toUpperCase()
  if (key === 'CREDIT') return 'txn-pill--credit'
  if (key === 'DEBIT') return 'txn-pill--debit'
  return 'txn-pill--neutral'
}

function deductionTypePillClass(type) {
  const key = String(type ?? '').toUpperCase()
  if (key === 'CALL') return 'txn-pill--call'
  if (key === 'WEB_CALL') return 'txn-pill--web'
  if (key === 'DAILY_RENTAL') return 'txn-pill--rental'
  if (key === 'TOP_UP') return 'txn-pill--topup'
  if (key === 'WHATSAPP_LOG') return 'txn-pill--whatsapp'
  return 'txn-pill--neutral'
}

function deductionTypeIcon(type) {
  const key = String(type ?? '').toUpperCase()
  if (key === 'CALL') return 'mdi-phone'
  if (key === 'WEB_CALL') return 'mdi-web'
  if (key === 'DAILY_RENTAL') return 'mdi-credit-card-minus'
  if (key === 'WHATSAPP_LOG') return 'mdi-whatsapp'
  if (key === 'TOP_UP') return 'mdi-cash'
  return 'mdi-tag-outline'
}

function formatPlainNumber(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return String(value)
  return Math.round(Math.abs(n)).toLocaleString()
}

function formatBalanceDisplay(value) {
  if (value == null || value === '') return '—'
  const n = Number(value)
  if (!Number.isFinite(n)) return String(value)
  return Math.round(Math.abs(n)).toLocaleString()
}

function hasCreditBalance(value) {
  return value != null && value !== ''
}

function creditBalanceTone(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return 'default'
  if (n <= 0) return 'error'

  const rental = Number(props.organization?.daily_rental)
  const lowThreshold =
    Number.isFinite(rental) && rental > 0 ? rental * 2 : 50
  if (n < lowThreshold) return 'warning'
  return 'success'
}

function creditBalanceClass(value) {
  const tone = creditBalanceTone(value)
  if (tone === 'success') return 'txn-balance--success'
  if (tone === 'warning') return 'txn-balance--warning'
  if (tone === 'error') return 'txn-balance--error'
  return ''
}

function creditBalanceTitle(value) {
  const amount = formatBalanceDisplay(value)
  const tone = creditBalanceTone(value)
  if (tone === 'error') return `${amount} credits — depleted`
  if (tone === 'warning') return `${amount} credits — low balance`
  return `${amount} credits`
}

function formatTransactionAmount(item) {
  if (item.amount == null || item.amount === '') return '—'
  return formatPlainNumber(item.amount)
}

function amountClass(item) {
  const key = String(item.transactionType ?? '').toUpperCase()
  if (key === 'CREDIT') return 'txn-amount--credit'
  if (key === 'DEBIT') return 'txn-amount--debit'
  return ''
}
</script>
